/**
 * Blog Post 20: Emergency Fund: How Much is Enough?
 * 緊急儲備金應準備多少？香港理財必讀
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

export default function BlogPost20() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';

    useEffect(() => {
        trackPageView('/blog/emergency-fund-how-much-hong-kong', 'Blog Post 20');
        document.title = isEnglish
            ? 'Emergency Fund: How Much is Enough? | Personal Finance Guide for Hong Kong'
            : '緊急儲備金應準備多少？香港理財必讀 | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? 'Emergency Fund Guide: How Much Should You Save in Hong Kong?'
        : '緊急儲備金應準備多少？香港理財必讀';

    const seoDescription = isEnglish
        ? 'Learn how much emergency fund to save in Hong Kong. Practical guide covering living expenses, job security, family size, and 3-6 month emergency fund benchmarks.'
        : '香港人應準備多少緊急儲備金？實用指南涵蓋生活支出、工作穩定性、家庭人數，以及3-6個月應急基金的標準。';

    const seoKeywords = isEnglish
        ? 'emergency fund Hong Kong, how much emergency fund, emergency fund calculation, 緊急儲備金, emergency savings, personal finance Hong Kong, financial planning 2026'
        : '緊急儲備金, 應急基金, 儲備金計算, 香港金融, 個人理財, 財務規劃 2026';

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords}
                canonicalUrl="https://www.paywise365.com/blog/emergency-fund-how-much-hong-kong"
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-05-10T00:00:00+08:00"
                modifiedDate="2026-05-10T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'Personal Finance' : '個人理財'}
                tags={isEnglish
                    ? ['Emergency Fund', 'Savings', 'Personal Finance', 'Financial Planning', 'Hong Kong']
                    : ['緊急儲備金', '儲蓄', '個人理財', '財務規劃', '香港']
                }
            />
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    color: 'white',
                    py: { xs: 6, md: 8 },
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ mb: 3, fontSize: '14px', opacity: 0.9 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Home' : '首頁'}
                        </a>
                        {' > '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Blog' : '資訊'}
                        </a>
                        {' > '}
                        <span style={{ opacity: 0.8 }}>
                            {isEnglish ? 'Emergency Fund' : '緊急儲備金'}
                        </span>
                    </Box>

                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? 'Emergency Fund: How Much is Enough?'
                            : '緊急儲備金應準備多少？'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
                        {isEnglish
                            ? 'A practical guide for Hong Kong personal finance planning'
                            : '香港個人理財規劃的實用指南'}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box sx={{ color: '#475569', lineHeight: 1.8, fontSize: '1rem' }}>
                    {isEnglish ? (
                        <>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 3 }}>
                                The Golden Rule: 3–6 Months of Living Expenses
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                Financial experts recommend holding 3 to 6 months of living expenses in an easily accessible emergency fund. This cushion helps you weather job loss, medical emergencies, or unexpected home repairs without derailing your long-term plans.
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                How to Calculate Your Number
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>Step 1: Add up monthly essentials</strong>
                            </Typography>
                            <Typography component="ul" sx={{ mb: 3, pl: 2 }}>
                                {['Rent or mortgage', 'Utilities & internet', 'Groceries & dining', 'Insurance & loans', 'Transport & childcare'].map((item) => (
                                    <li key={item} style={{ marginBottom: '8px' }}>
                                        {item}
                                    </li>
                                ))}
                            </Typography>

                            <Typography sx={{ mb: 3 }}>
                                <strong>Step 2: Apply the multiplier</strong>
                            </Typography>
                            <ul style={{ marginBottom: '16px' }}>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>Stable job + low dependents:</strong> Aim for 3 months
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>Freelancer/contractor:</strong> Aim for 6+ months
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>Single income → family:</strong> Aim for 6 months
                                </li>
                            </ul>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                Where to Keep It
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                Use a separate <strong>high-yield savings account or easy-access deposit</strong> (not stocks or long-term investments). In Hong Kong, many banks offer 3–4% interest on savings accounts—free money while you wait.
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                Quick Example
                            </Typography>
                            <Typography sx={{ mb: 3, bgcolor: '#f8fafc', p: 2.5, borderRadius: 1, fontFamily: 'monospace' }}>
                                Monthly expenses: HK$25,000<br />
                                × 6 months = <strong>HK$150,000</strong> emergency fund target
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                Key Takeaways
                            </Typography>
                            <ul>
                                <li style={{ marginBottom: '8px' }}>Start with 3 months; increase to 6 if your income is irregular</li>
                                <li style={{ marginBottom: '8px' }}>Keep it accessible—not in fixed deposits or illiquid assets</li>
                                <li style={{ marginBottom: '8px' }}>Build it gradually; even HK$500/month compounds over time</li>
                                <li style={{ marginBottom: '8px' }}>Review annually and adjust for life changes (marriage, kids, job)</li>
                            </ul>

                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#64748b' }}>
                                <strong>Published:</strong> 10 May 2026 | <strong>Read time:</strong> 3 minutes
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 3 }}>
                                黃金法則：準備 3–6 個月生活費
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                財務專家建議預留 3 至 6 個月的生活費作為應急基金。這個緩衝幫助你應對失業、醫療突急或居家維修，而毋須打亂長期財務計劃。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                如何計算金額
                            </Typography>
                            <Typography sx={{ mb: 2 }}>
                                <strong>第一步：加總每月基本開支</strong>
                            </Typography>
                            <Typography component="ul" sx={{ mb: 3, pl: 2 }}>
                                {['租金或按揭', '水電網絡', '糧食及用餐', '保險及貸款', '交通及託兒'].map((item) => (
                                    <li key={item} style={{ marginBottom: '8px' }}>
                                        {item}
                                    </li>
                                ))}
                            </Typography>

                            <Typography sx={{ mb: 3 }}>
                                <strong>第二步：應用倍數</strong>
                            </Typography>
                            <ul style={{ marginBottom: '16px' }}>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>穩定工作 + 少撫養人：</strong>準備 3 個月
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>自由工作者：</strong>準備 6 個月或以上
                                </li>
                                <li style={{ marginBottom: '8px' }}>
                                    <strong>單一收入轉為養家：</strong>準備 6 個月
                                </li>
                            </ul>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                放在哪裡
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                使用獨立的<strong>高息存款戶口或易取存款</strong>（非股票或長期投資）。香港許多銀行提供 3–4% 存款利息—等待期間有免費利息。
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                快速例子
                            </Typography>
                            <Typography sx={{ mb: 3, bgcolor: '#f8fafc', p: 2.5, borderRadius: 1, fontFamily: 'monospace' }}>
                                每月開支：HK$25,000<br />
                                × 6 個月 = <strong>HK$150,000</strong> 應急基金目標
                            </Typography>

                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e', mt: 4 }}>
                                重點摘要
                            </Typography>
                            <ul>
                                <li style={{ marginBottom: '8px' }}>從 3 個月開始；若收入不穩定則增至 6 個月</li>
                                <li style={{ marginBottom: '8px' }}>保持易取——不要放在定期存款或流動性差的資產</li>
                                <li style={{ marginBottom: '8px' }}>逐步累積；即使每月 HK$500 也會累積成可觀金額</li>
                                <li style={{ marginBottom: '8px' }}>每年檢查並因應人生變化（結婚、生子、轉工）調整</li>
                            </ul>

                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0', fontSize: '0.9rem', color: '#64748b' }}>
                                <strong>發佈日期：</strong>2026 年 5 月 10 日 | <strong>閱讀時間：</strong>3 分鐘
                            </Typography>
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
}
